import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { 
  Home, User, Heart, Star, Menu, X, Crown, 
  Settings, LogOut, CreditCard, Bell, ChevronRight, Hand,
  Shield, Languages, HelpCircle, Calendar, Sparkles,
  Clock, Compass, Sun, Mic
} from 'lucide-react';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

// Admin emails
const ADMIN_EMAILS = ['demo@nakshatra.com', 'admin@nakshatra.com'];
const isAdminUser = (email) => ADMIN_EMAILS.includes(email?.toLowerCase());

// Shubh Bottom Navigation - 5 tabs
export const AppBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();

  const navItems = [
    { path: '/dashboard', icon: Home, label: language === 'hi' ? 'होम' : 'Home' },
    { path: '/timeline', icon: Clock, label: language === 'hi' ? 'समयरेखा' : 'Timeline' },
    { path: '/janam-kundli', icon: Sun, label: language === 'hi' ? 'कुंडली' : 'Kundli', isSpecial: true },
    { path: '/rashifal', icon: Star, label: language === 'hi' ? 'राशिफल' : 'Rashifal' },
    { path: '/profiles', icon: User, label: language === 'hi' ? 'आप' : 'You' },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-50" 
      style={{ 
        paddingBottom: 'env(safe-area-inset-bottom)',
        background: 'rgba(255, 255, 255, 0.78)',
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        borderTop: '1px solid rgba(40, 38, 34, 0.08)',
        boxShadow: '0 -4px 20px rgba(82, 58, 26, 0.08)'
      }}
      data-testid="bottom-navigation"
    >
      <div className="flex items-center justify-around h-[68px] max-w-[500px] mx-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          if (item.isSpecial) {
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="flex flex-col items-center justify-center flex-1 h-full relative"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <motion.div 
                  className="flex items-center justify-center -mt-3"
                  style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #FFD066 0%, #F5B942 100%)',
                    borderRadius: '50%',
                    boxShadow: '0 4px 12px rgba(245, 185, 66, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                  }}
                  whileTap={{ scale: 0.92 }}
                >
                  <Icon 
                    className="w-[22px] h-[22px]" 
                    style={{ color: '#282622' }}
                  />
                </motion.div>
                <span 
                  className="text-[11px] font-medium -mt-1"
                  style={{ color: active ? '#C84B32' : '#9A948C' }}
                >
                  {item.label}
                </span>
              </button>
            );
          }
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="flex flex-col items-center justify-center flex-1 h-full gap-1 relative"
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              {active && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-b-full"
                  style={{
                    background: 'linear-gradient(90deg, #C84B32, #F5B942)',
                  }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
              <motion.div whileTap={{ scale: 0.9 }}>
                <Icon 
                  className="w-[22px] h-[22px]" 
                  style={{ 
                    color: active ? '#C84B32' : '#9A948C',
                    strokeWidth: active ? 2.25 : 1.75
                  }}
                />
              </motion.div>
              <span 
                className="text-[11px] font-medium"
                style={{ color: active ? '#C84B32' : '#9A948C' }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

// Main App Layout Component
const AppLayout = ({ children }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);

  const isAdmin = isAdminUser(user?.email);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  const menuItems = [
    { icon: User, label: language === 'hi' ? 'मेरी प्रोफ़ाइल' : 'My Profiles', action: () => navigate('/profiles') },
    { icon: Mic, label: language === 'hi' ? 'वैदिक गुरु' : 'Vedic Guru', action: () => navigate('/vedic-guru'), highlight: true, badge: 'Voice AI' },
    { icon: Star, label: language === 'hi' ? 'जन्म कुंडली' : 'Janam Kundli', action: () => navigate('/janam-kundli') },
    { icon: Heart, label: language === 'hi' ? 'कुंडली मिलान' : 'Kundli Milan', action: () => navigate('/kundli-milan') },
    { icon: Hand, label: language === 'hi' ? 'हस्तरेखा' : 'Palm Reading', action: () => navigate('/palm-reading') },
    { icon: Calendar, label: language === 'hi' ? 'हिंदू पंचांग' : 'Hindu Panchang', action: () => navigate('/hindu-calendar') },
    { icon: Crown, label: language === 'hi' ? 'सदस्यता' : 'Subscription', action: () => navigate('/subscription') },
    { icon: HelpCircle, label: language === 'hi' ? 'सहायता' : 'Help', action: () => navigate('/help') },
  ];

  if (isAdmin) {
    menuItems.push({ 
      icon: Shield, 
      label: language === 'hi' ? 'एडमिन पैनल' : 'Admin Panel', 
      action: () => navigate('/admin'), 
      badge: 'Admin' 
    });
  }

  return (
    <div 
      className="min-h-screen"
      style={{ 
        background: '#FFF9EF',
        paddingBottom: 'calc(88px + env(safe-area-inset-bottom))'
      }}
    >
      {/* Header */}
      <header 
        className="sticky top-0 z-40 px-5 py-3"
        style={{ 
          background: 'rgba(255, 249, 239, 0.95)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(40, 38, 34, 0.08)',
          paddingTop: 'calc(12px + env(safe-area-inset-top))'
        }}
      >
        <div className="flex items-center justify-between h-12">
          {/* Menu Button */}
          <button
            onClick={() => setMenuOpen(true)}
            className="p-2 rounded-lg"
            style={{ background: 'transparent' }}
            data-testid="menu-button"
          >
            <Menu className="w-5 h-5" style={{ color: '#282622' }} />
          </button>

          {/* Title */}
          <h1 
            className="text-xl font-medium"
            style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#282622' }}
          >
            Nakshatra
          </h1>

          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{ background: '#FCE9D9' }}
            data-testid="language-toggle"
          >
            <Languages className="w-4 h-4" style={{ color: '#6B6560' }} />
            <span 
              className="text-sm font-medium"
              style={{ color: language === 'hi' ? '#C84B32' : '#282622' }}
            >
              {language === 'en' ? 'EN' : 'हि'}
            </span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-5 py-4">
        {children}
      </main>

      {/* Bottom Navigation */}
      <AppBottomNav />

      {/* Slide-out Menu */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[80]"
            />
            
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-0 top-0 bottom-0 w-[300px] z-[90]"
              style={{ 
                paddingTop: 'env(safe-area-inset-top)',
                background: '#FFF9EF'
              }}
            >
              {/* Menu Header */}
              <div 
                className="p-5 border-b"
                style={{ borderColor: 'rgba(40, 38, 34, 0.08)' }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center"
                      style={{ 
                        background: 'linear-gradient(135deg, #F5B942 0%, #C84B32 100%)'
                      }}
                    >
                      <span className="text-white text-lg font-bold">
                        {getInitials(user?.name)}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: '#282622' }}>
                        {user?.name || 'User'}
                      </p>
                      <p className="text-xs" style={{ color: '#9A948C' }}>
                        {user?.email}
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setMenuOpen(false)} 
                    className="p-2 rounded-full"
                    style={{ background: 'rgba(40, 38, 34, 0.08)' }}
                  >
                    <X className="w-5 h-5" style={{ color: '#6B6560' }} />
                  </button>
                </div>
              </div>

              {/* Menu Items */}
              <div className="p-3 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                {menuItems.map((item, i) => (
                  <button
                    key={i}
                    onClick={() => { item.action(); setMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all"
                    style={{
                      background: item.highlight 
                        ? 'linear-gradient(135deg, rgba(234, 88, 12, 0.08) 0%, rgba(245, 185, 66, 0.08) 100%)'
                        : 'transparent',
                      border: item.highlight ? '1px solid rgba(234, 88, 12, 0.2)' : 'none'
                    }}
                  >
                    <item.icon 
                      className="w-5 h-5" 
                      style={{ color: item.highlight ? '#C84B32' : '#6B6560' }}
                    />
                    <span 
                      className="text-sm font-medium flex-1 text-left"
                      style={{ color: item.highlight ? '#C84B32' : '#282622' }}
                    >
                      {item.label}
                    </span>
                    {item.badge && (
                      <span 
                        className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                        style={{
                          background: item.badge === 'Admin' 
                            ? 'rgba(147, 51, 234, 0.1)'
                            : 'rgba(234, 88, 12, 0.15)',
                          color: item.badge === 'Admin' ? '#7C3AED' : '#C84B32'
                        }}
                      >
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}

                {/* Logout */}
                <div className="pt-4 mt-4 border-t" style={{ borderColor: 'rgba(40, 38, 34, 0.08)' }}>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl"
                    style={{ background: 'rgba(239, 68, 68, 0.08)' }}
                  >
                    <LogOut className="w-5 h-5 text-red-500" />
                    <span className="text-sm font-medium text-red-500">
                      {language === 'hi' ? 'लॉग आउट' : 'Logout'}
                    </span>
                  </button>
                </div>
              </div>
              
              {/* Footer */}
              <div 
                className="absolute bottom-0 left-0 right-0 p-4 border-t"
                style={{ borderColor: 'rgba(40, 38, 34, 0.08)', background: '#FFF9EF' }}
              >
                <p className="text-center text-[10px]" style={{ color: '#9A948C' }}>
                  Nakshatra 2.0 • Made with ❤️ in India
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AppLayout;
